import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ShieldAlert, Unlock, CheckCircle2, Info } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const typeColor = {
  copy: "text-rose-700 border-rose-200 bg-rose-50",
  paste: "text-rose-700 border-rose-200 bg-rose-50",
  cut: "text-rose-700 border-rose-200 bg-rose-50",
  shortcut: "text-amber-700 border-amber-200 bg-amber-50",
  context: "text-amber-700 border-amber-200 bg-amber-50",
  drag: "text-amber-700 border-amber-200 bg-amber-50",
};

export default function AdminViolations() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  // Employees currently animating from "just unlocked" → "removed". We keep the
  // rows visible with a green "UNLOCKED" badge for a couple of seconds before
  // refetching so the admin can clearly see WHICH candidate got released.
  const [justUnlocked, setJustUnlocked] = useState({}); // { [employee_id]: {name, count} }
  const [busyEid, setBusyEid] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/admin/violations?limit=200");
    setRows(data.items); setTotal(data.total);
  }, []);
  useEffect(() => { load(); }, [load]);

  const unlock = async (eid, name) => {
    if (busyEid) return;
    setBusyEid(eid);
    // Count rows for this employee so we can show a specific message.
    const affected = rows.filter((r) => r.employee_id === eid).length;
    try {
      await api.post(`/admin/employees/${eid}/unlock`);
      // Optimistic marker so the admin sees the change with clarity
      setJustUnlocked((jl) => ({ ...jl, [eid]: { name, count: affected, at: Date.now() } }));
      toast.success(
        `${name || "Candidate"} unlocked · ${affected} violation ${affected === 1 ? "entry" : "entries"} cleared from this list`,
        { duration: 4500 }
      );
      // Wait a moment so the badge is visible, then refetch (rows for this
      // employee will drop off the server payload since the backend filter is
      // "only currently-locked candidates").
      setTimeout(async () => {
        await load();
        setJustUnlocked((jl) => {
          const { [eid]: _drop, ...rest } = jl;
          return rest;
        });
      }, 1800);
    } catch (e) {
      toast.error("Unlock failed. Try again.");
    } finally {
      setBusyEid(null);
    }
  };

  // Group counts for the header info strip.
  const uniqueLockedCount = useMemo(() => new Set(rows.map((r) => r.employee_id)).size, [rows]);

  return (
    <div>
      <div className="flex items-end justify-between flex-wrap gap-3 mb-4">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Security Violations</h1>
          <p className="text-zinc-500 mt-2 text-sm">
            <b className="tabular-nums text-zinc-800">{uniqueLockedCount}</b> candidate{uniqueLockedCount === 1 ? "" : "s"} currently locked · <b className="tabular-nums text-zinc-800">{total}</b> violation event{total === 1 ? "" : "s"}
          </p>
        </div>
        <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-rose-50 text-rose-700 border border-rose-200 text-xs font-semibold">
          <ShieldAlert size={12} /> Copy / Paste / Cut violations auto-lock candidates
        </span>
      </div>

      {/* Helpful explainer so admin knows what "row disappear" means */}
      <div className="mb-4 flex items-start gap-2 rounded-xl bg-sky-50 border border-sky-200 px-3 py-2 text-[12px] text-sky-800">
        <Info size={14} className="mt-0.5 shrink-0" />
        <div>
          Yeh page sirf <b>currently locked</b> candidates dikhata hai. Jaise hi aap kisi ka <b>Unlock</b> button dabate ho — us candidate ki saari rows list se hat jaati hain (audit trail secure rehta hai, sirf display se hatti hain).
        </div>
      </div>

      <div className="rounded-2xl bg-white border border-[var(--line)] overflow-hidden shadow-sm">
        <div className="max-h-[600px] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-zinc-50/95 backdrop-blur">
              <tr>
                {["Candidate", "Email", "Violation", "Date & Time", "Status", "Action"].map((h) => (
                  <th key={h} className="text-left px-5 py-3 text-[11px] uppercase tracking-[0.12em] text-zinc-500 font-semibold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const isContext = r.type === "context";
                const wc = r.warning_count;
                const wmax = r.warning_limit || 10;
                const isLocked = !isContext || (typeof wc === "number" && wc >= wmax);
                const marker = justUnlocked[r.employee_id];
                const releasing = !!marker;
                return (
                <tr
                  key={r.id}
                  data-testid={`violation-row-${r.id}`}
                  className={`border-t border-[var(--line)] transition-colors duration-300 ${releasing ? "bg-emerald-50/70" : "hover:bg-zinc-50"}`}
                >
                  <td className="px-5 py-3 font-medium">{r.first_name} {r.last_name}</td>
                  <td className="px-5 py-3 text-zinc-500">{r.email}</td>
                  <td className="px-5 py-3">
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full border text-xs font-semibold ${typeColor[r.type] || "bg-zinc-50 border-[var(--line)]"}`}>
                      {r.type.toUpperCase()}
                      {isContext && typeof wc === "number" && (
                        <span className="ml-1.5 font-mono">{wc}/{wmax}</span>
                      )}
                    </span>
                    {r.detail && <span className="block text-[10px] text-zinc-400 mt-1 font-mono">{r.detail}</span>}
                  </td>
                  <td className="px-5 py-3 text-xs text-zinc-500">{new Date(r.occurred_at).toLocaleString()}</td>
                  <td className="px-5 py-3">
                    {releasing ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200 text-[10px] font-semibold">
                        <CheckCircle2 size={10} /> UNLOCKED
                      </span>
                    ) : isLocked ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-rose-50 text-rose-700 border border-rose-200 text-[10px] font-semibold">LOCKED</span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200 text-[10px] font-semibold">WARNING</span>
                    )}
                  </td>
                  <td className="px-5 py-3">
                    {releasing ? (
                      <span className="text-[11px] text-emerald-700">Removing…</span>
                    ) : isLocked ? (
                      <button
                        onClick={() => unlock(r.employee_id, `${r.first_name} ${r.last_name}`.trim())}
                        data-testid={`violation-unlock-${r.id}`}
                        disabled={busyEid === r.employee_id}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[var(--ink)] text-white text-xs font-semibold hover:bg-[var(--accent)] disabled:opacity-50"
                      >
                        <Unlock size={12} /> {busyEid === r.employee_id ? "Unlocking…" : "Unlock"}
                      </button>
                    ) : (
                      <span className="text-[11px] text-zinc-400">—</span>
                    )}
                  </td>
                </tr>
                );
              })}
              {!rows.length && (
                <tr>
                  <td colSpan={6} className="px-6 py-14 text-center">
                    <div className="inline-flex flex-col items-center gap-2 text-zinc-500 text-sm">
                      <CheckCircle2 size={22} className="text-emerald-500" />
                      <div className="font-semibold text-zinc-700">No candidate is currently locked</div>
                      <div className="text-xs">Sab candidate free hain — jo naye locks aayenge woh yahi dikhengey.</div>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
